import * as t from 'io-ts';
import {
  createJsonRequest,
  FetchClientWithCredentials,
  ioTransformer,
} from '@veupathdb/http-utils';
import { DerivedVariable } from '../types/analysis';
import { Filter } from '../types/filter';
import { VariableMapping } from './DataClient/types';

interface ComputeSpec {
  studyId: string;
  filters?: Filter[];
  derivedVariables: DerivedVariable[];
  config: unknown;
}

export type JobStatusReponse = t.TypeOf<typeof JobStatusReponse>;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const JobStatusReponse = t.type({
  jobID: t.string,
  status: t.keyof({
    'no-such-job': null,
    queued: null,
    'in-progress': null,
    complete: null,
    failed: null,
    expired: null,
  }),
});

export type ComputedVariableMetadata = t.TypeOf<
  typeof ComputedVariableMetadata
>;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ComputedVariableMetadata = t.type({
  variables: t.array(VariableMapping),
});

export class ComputeClient extends FetchClientWithCredentials {
  /**
   * Creates a job for a given compute plugin and spec, if one
   * does not already exists, and returns a job status record.
   *
   * @param computeName Name of compute plugin
   * @param computeSpec Properties of compute job
   * @returns {Promise<JobStatusReponse>}
   */
  getJobStatus(
    computeName: string,
    computeSpec: ComputeSpec
  ): Promise<JobStatusReponse> {
    return this.postJobStatus(computeName, computeSpec, false);
  }
  createJob(
    computeName: string,
    computeSpec: ComputeSpec
  ): Promise<JobStatusReponse> {
    return this.postJobStatus(computeName, computeSpec, true);
  }
  getComputedVariableMetadata(
    computeName: string,
    computeSpec: ComputeSpec
  ): Promise<ComputedVariableMetadata> {
    return this.fetch(
      createJsonRequest({
        method: 'POST',
        path: `/computes/${computeName}/meta`,
        body: computeSpec,
        transformResponse: async (body: unknown) => {
          // The /meta endpoint may return Content-Type: text/plain,
          // so the body may arrive as a string rather than parsed JSON.
          const parsed = typeof body === 'string' ? JSON.parse(body) : body;
          return ioTransformer(ComputedVariableMetadata)(parsed);
        },
      })
    );
  }
  private postJobStatus(
    computeName: string,
    computeSpec: ComputeSpec,
    autostart: boolean
  ): Promise<JobStatusReponse> {
    return this.fetch(
      createJsonRequest({
        method: 'POST',
        path: `/computes/${computeName}?autostart=${autostart}`,
        body: computeSpec,
        transformResponse: ioTransformer(JobStatusReponse),
      })
    );
  }
}
